import { createContext, useState, useContext, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useAuth } from "./AuthContext";

const getStored = (key, fallback) => {
  try {
    const item = localStorage.getItem(key);
    return item !== null ? JSON.parse(item) : fallback;
  } catch {
    return fallback;
  }
};
//  Descomentar cuando se implementen los servicios
// import { useAnnualHotspots, useMonthlyAnalytics } from "../hooks/useReactQuery";

const LayoutContext = createContext(null);

export const LayoutProvider = ({ children }) => {
  const [isNavExpanded, setIsNavExpanded] = useState(() => getStored("layout_isNavExpanded", true));
  const [theme, setTheme] = useState(() => getStored("layout_theme", "light"));
  const [language, setLanguage] = useState(() => getStored("layout_language", "es"));
  const [primaryColor, setPrimaryColor] = useState(() => getStored("layout_primaryColor", "#1976d2"));
  const [fontSize, setFontSize] = useState(() => getStored("layout_fontSize", "normal"));
  const [headerTitle, setHeaderTitle] = useState(() => getStored("layout_headerTitle", ""));
  const [isHeaderVisible, setIsHeaderVisible] = useState(() => getStored("layout_isHeaderVisible", true));
  const [headerFilters, setHeaderFilters] = useState(null);
  const [defaultFilters, setDefaultFilters] = useState(null);
  
  const { user } = useAuth();
  // const isAuthenticated = !!user;
  
  // Nuevo estado para la fecha
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  });

  // Función para obtener la fecha actual formateada
  const getCurrentDate = useMemo(() => {
    const date = dayjs(selectedDate).toDate();
    const options = {
      weekday: "long",
      year: "numeric", 
      month: "long",
      day: "numeric",
    };
    return date
      .toLocaleDateString("es-ES", options)
      .replace(/^./, (match) => match.toUpperCase());
  }, [selectedDate]);

  // Función para cambiar la fecha seleccionada
  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  // Verificar si el usuario puede realizar consultas de datos
  const canFetchData = useMemo(() => {
    return !!user && (user.role_id === 3 || user.role_id === 4);
  }, [user]);


  // Consultas adicionales también controladas por autenticación
  // TODO: Actualizar en el futuro cuando se implementen los servicios
  // const {
  //   data: monthlyAnalytics,
  //   isLoading,
  //   refetch: refetchDateData,
  // } = useMonthlyAnalytics(selectedDate, canFetchData, isAuthenticated);

  // const {
  //   data: annualHotspots,
  // } = useAnnualHotspots(selectedDate, canFetchData, isAuthenticated);

  // Valores temporales mientras se implementan los servicios
  const monthlyAnalytics = useMemo(() => [], []);
  const annualHotspots = useMemo(() => [], []);
  const isLoading = false;
  const refetchDateData = useCallback(() => {}, []);

  useEffect(() => { localStorage.setItem("layout_isNavExpanded", JSON.stringify(isNavExpanded)); }, [isNavExpanded]);
  useEffect(() => { localStorage.setItem("layout_theme", JSON.stringify(theme)); }, [theme]);
  useEffect(() => { localStorage.setItem("layout_language", JSON.stringify(language)); }, [language]);
  useEffect(() => { localStorage.setItem("layout_primaryColor", JSON.stringify(primaryColor)); }, [primaryColor]);
  useEffect(() => { localStorage.setItem("layout_fontSize", JSON.stringify(fontSize)); }, [fontSize]);
  useEffect(() => { localStorage.setItem("layout_headerTitle", JSON.stringify(headerTitle)); }, [headerTitle]);
  useEffect(() => { localStorage.setItem("layout_isHeaderVisible", JSON.stringify(isHeaderVisible)); }, [isHeaderVisible]);

  const toggleNav = useCallback(() => {
    setIsNavExpanded(!isNavExpanded);
  }, [isNavExpanded]);
  
  const toggleTheme = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [theme]);

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  const changePrimaryColor = (newColor) => {
    setPrimaryColor(newColor);
  };

  const changeFontSize = (size) => {
    setFontSize(size);
  };

  // Valores del contexto actualizados
  const contextValue = useMemo(
    () => ({
      isNavExpanded,
      theme,
      language,
      primaryColor,
      fontSize,
      selectedDate,
      getCurrentDate,
      headerTitle,
      setHeaderTitle,
      isHeaderVisible,
      headerFilters,
      setHeaderFilters,
      defaultFilters,
      setDefaultFilters,
      toggleNav,
      toggleTheme,
      changeLanguage,
      changePrimaryColor,
      changeFontSize,
      handleDateChange,
      refetchDateData,
      annualHotspots,
      isLoading,
      monthlyAnalytics,
      setIsHeaderVisible,
      canFetchData
    }),
    [
      isNavExpanded,
      theme,
      language,
      primaryColor,
      fontSize,
      selectedDate,
      getCurrentDate,
      headerTitle,
      isHeaderVisible,
      headerFilters,
      defaultFilters,
      annualHotspots,
      isLoading,
      setIsHeaderVisible,
      toggleNav,
      toggleTheme,
      refetchDateData,
      monthlyAnalytics,
      canFetchData
    ]
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout debe utilizarse dentro de un LayoutProvider");
  }
  return context;
};

LayoutProvider.propTypes = {
  children: PropTypes.node.isRequired,
};